import { AppScreen } from "common/Home/General/types.ts";
import {
  ADMIN_SIGNALS_PATH,
  ADMIN_TRADING_CONFIGURATION_PATH,
  AFFILIATION_PATH,
  BUSINESS_PROFILE_PATH,
  BASE_BILLING_SETTINGS_PATH,
  BASE_TRADES_PATH,
  CREATE_SIGNAL_PATH,
  CREATE_TRADE_PATH,
  DASHBOARD_PATH,
  DCA_PUBLIC_BOTS_PATH,
  FINANCE_PATH,
  USER_SIGNALS_PATH,
  GROUP_MANAGEMENT_PATH,
  INVOICES_PATH,
  MARKETPLACE_PATH,
  MCP_PATH,
  MY_GROUPS_PATH,
  NOTIFICATIONS_SETTINGS_PATH,
  OPEN_TRADES_PATH,
  PORTFOLIO_ACCOUNTS_PATH,
  PORTFOLIO_ASSETS_PATH,
  PORTFOLIO_PATH,
  PORTFOLIO_POSITIONS_PATH,
  SECURITY_SETTINGS_PATH,
  SETTINGS_PATH,
  USERS_MANAGEMENT_PATH,
  WATCHLIST_PATH,
} from "common/constants.ts";
import {
  AccountsIcon,
  BillingIcon,
  DashboardIcon,
  GroupManagementIcon,
  GroupsIcon,
  SignalsIcon,
  Sliders,
} from "common/Icons/Icons.tsx";
import { MenuItemConfig, SiderGroup } from "types";
import React from "react";
import { BotType, LanguageNameSpaces } from "@shared/types";
import {
  BOT_TYPE_TO_BASE_BACKTESTING_PATH,
  BOT_TYPE_TO_BASE_MY_BOTS_PATH,
  BOT_TYPE_TO_BASE_PATH,
} from "features/bots/common/constants.ts";
import { BOT_TYPES } from "@shared/common/constants";
import {
  BOT_TYPE_TO_ICON,
  BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON,
  DARK_BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON,
} from "features/settings/common/constants.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import {
  BankOutlined,
  CrownOutlined,
  DollarOutlined,
  EyeOutlined,
  FundProjectionScreenOutlined,
  IdcardOutlined,
  PieChartOutlined,
  SettingOutlined,
  ShopOutlined,
  SwapOutlined,
  UserOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { getGroupManagementRoute } from "utils/routes.tsx";
import styled, { css } from "styled-components";
import {
  ArrowLeftRight,
  ChartPie,
  CreditCard,
  Crown,
  Eye,
  IdCard,
  Landmark,
  LayoutDashboard,
  LucideIcon,
  Monitor,
  Plug,
  Activity,
  Receipt,
  Settings,
  SlidersHorizontal,
  Store,
  Users,
  UsersRound,
  Wallet,
} from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import { NEW_LOOK_BOT_TYPE_TO_ICON } from "common/NewLook/botTypeIcons.ts";

const NEW_LOOK_ICON_SIZE = 16;
const NEW_LOOK_ICON_STROKE_WIDTH = 1.75;
const newLookIcon = (Icon: LucideIcon) => (
  <Icon size={NEW_LOOK_ICON_SIZE} strokeWidth={NEW_LOOK_ICON_STROKE_WIDTH} />
);
const lookIcon = (classic: React.JSX.Element, Icon: LucideIcon) => (
  <LookIcon classic={classic} newLook={newLookIcon(Icon)} />
);

export const HELP_MENU_ITEM_KEY = "help";
export const INTERCOM_MENU_ITEM_KEY = "intercom_links";
export const SUPPORT_MENU_ITEM_KEY = "support";
export const WORKSPACE_MENU_ITEM_KEY = "workspace";
export const ADMIN_WORKSPACE_MENU_ITEM_KEY = "admin-workspace";
export const USER_WORKSPACE_MENU_ITEM_KEY = "user-workspace";
export const getBotItemKey = (botType: BotType) => `bots_${botType}`;
export const getMyBotItemKey = (botType: BotType) => `${botType}_my_bots`;
export const getBacktestingItemKey = (botType: BotType) =>
  `${botType}_backtesting`;
export const BILLING_MENU_ITEM = {
  key: AppScreen.BILLING,
  name: "page_title.billing",
  link: BASE_BILLING_SETTINGS_PATH,
  routerMatch: BASE_BILLING_SETTINGS_PATH,
  icon: lookIcon(<BillingIcon />, CreditCard),
  group: SiderGroup.OTHER,
};
export const INVOICES_MENU_ITEM = {
  key: AppScreen.INVOICES,
  name: "page_title.invoices",
  link: INVOICES_PATH,
  routerMatch: INVOICES_PATH,
  icon: lookIcon(<DollarOutlined />, Receipt),
  group: SiderGroup.BUSINESS_OPERATIONS,
};
export const BUSINESS_PROFILE_MENU_ITEM = {
  key: AppScreen.BUSINESS_PROFILE,
  name: "page_title.business_profile",
  link: BUSINESS_PROFILE_PATH,
  routerMatch: BUSINESS_PROFILE_PATH,
  icon: lookIcon(<IdcardOutlined />, IdCard),
  group: SiderGroup.BUSINESS_OPERATIONS,
};
const ACCOUNT_AND_PRIVACY_SUB_MENU_ITEM = {
  key: AppScreen.ACCOUNT_AND_SECURITY,
  name: "page_title.account_and_security",
  link: SECURITY_SETTINGS_PATH,
  routerMatch: SECURITY_SETTINGS_PATH,
};
const NOTIFICATIONS_SUB_MENU = {
  key: AppScreen.NOTIFICATIONS_MANAGEMENT,
  name: "page_title.notifications_management",
  link: NOTIFICATIONS_SETTINGS_PATH,
  routerMatch: NOTIFICATIONS_SETTINGS_PATH,
};
export const SETTINGS_MENU_ITEM = {
  key: AppScreen.SETTINGS,
  name: "page_title.settings",
  icon: lookIcon(<SettingOutlined />, Settings),
  routerMatch: SETTINGS_PATH,
  link: SECURITY_SETTINGS_PATH,
  subMenu: [ACCOUNT_AND_PRIVACY_SUB_MENU_ITEM, NOTIFICATIONS_SUB_MENU],
  group: SiderGroup.OTHER,
};
export const DASHBOARD_MENU_ITEM = {
  key: AppScreen.DASHBOARD,
  name: "page_title.dashboard",
  icon: lookIcon(<DashboardIcon />, LayoutDashboard),
  link: DASHBOARD_PATH,
  routerMatch: DASHBOARD_PATH,
};
export const ADMIN_GROUP_MANAGEMENT_MENU_ITEM = {
  key: AppScreen.GROUP_MANAGEMENT,
  icon: lookIcon(<GroupManagementIcon />, Users),
  link: getGroupManagementRoute(),
  name: "page_title.users_management",
  routerMatch: GROUP_MANAGEMENT_PATH,
};
export const ADMIN_SIGNALS_MENU_ITEM = {
  key: AppScreen.ADMIN_SIGNALS,
  icon: lookIcon(<SignalsIcon />, Activity),
  name: "page_title.signals",
  link: ADMIN_SIGNALS_PATH,
  routerMatch: ADMIN_SIGNALS_PATH,
};
const largeIconStyles = css`
  &&&& {
    font-size: 18px !important;
    margin-right: -2px;
    margin-left: -1px;
  }
`;
const StyledAccountsIcon = styled(AccountsIcon)`
  ${largeIconStyles};
`;
const StyledGroupsIcon = styled(GroupsIcon)`
  ${largeIconStyles};
`;
export const ACCOUNTS_MENU_ITEM = {
  icon: lookIcon(<StyledAccountsIcon />, Wallet),
  key: AppScreen.ACCOUNTS,
  name: "page_title.accounts",
  link: PORTFOLIO_ACCOUNTS_PATH,
  routerMatch: PORTFOLIO_ACCOUNTS_PATH,
  group: SiderGroup.PORTFOLIO,
};
export const ASSETS_MENU_ITEM = {
  key: AppScreen.ASSETS,
  name: "page_title.assets",
  link: PORTFOLIO_ASSETS_PATH,
  routerMatch: PORTFOLIO_ASSETS_PATH,
};
export const POSITIONS_MENU_ITEM = {
  key: AppScreen.POSITIONS,
  name: "page_title.positions",
  link: PORTFOLIO_POSITIONS_PATH,
  routerMatch: PORTFOLIO_POSITIONS_PATH,
};
export const USERS_MANAGEMENT_MENU_ITEM = {
  key: AppScreen.USERS_MANAGEMENT,
  name: "page_title.users_management",
  icon: lookIcon(<GroupManagementIcon />, Users),
  link: USERS_MANAGEMENT_PATH,
  routerMatch: USERS_MANAGEMENT_PATH,
  group: SiderGroup.BUSINESS_OPERATIONS,
};
export const PORTFOLIO_MENU_ITEM = {
  key: AppScreen.PORTFOLIO,
  name: "page_title.portfolio",
  icon: lookIcon(<PieChartOutlined />, ChartPie),
  routerMatch: PORTFOLIO_PATH,
  link: PORTFOLIO_ASSETS_PATH,
  subMenu: [ASSETS_MENU_ITEM, POSITIONS_MENU_ITEM],
  group: SiderGroup.PORTFOLIO,
};
export const TRADES_MENU_ITEM = {
  icon: lookIcon(<SwapOutlined />, ArrowLeftRight),
  name: "page_title.trades",
  key: AppScreen.TRADES,
  routerMatch: BASE_TRADES_PATH,
  link: OPEN_TRADES_PATH,
};
export const TRADING_TERMINAL_MENU_ITEM = {
  icon: lookIcon(<FundProjectionScreenOutlined />, Monitor),
  name: "page_title.trading_terminal",
  key: "tradingTerminal",
  routerMatch: CREATE_TRADE_PATH,
  link: CREATE_TRADE_PATH,
  group: SiderGroup.TRADING,
};
export const SIGNALS_MENU_ITEM = {
  icon: lookIcon(<SignalsIcon />, Activity),
  name: "page_title.signals",
  link: USER_SIGNALS_PATH,
  key: AppScreen.SIGNALS,
  routerMatch: USER_SIGNALS_PATH,
  group: SiderGroup.MONITORING,
  subMenu: [
    {
      key: "group-signals",
      name: "page_title.group_signals",
      link: USER_SIGNALS_PATH,
      routerMatch: USER_SIGNALS_PATH,
      exactMatch: true,
    },
    {
      key: AppScreen.SIGNAL_TERMINAL,
      name: "page_title.signals_terminal",
      link: CREATE_SIGNAL_PATH,
      routerMatch: CREATE_SIGNAL_PATH,
    },
  ],
};
export const WATCHLIST_MENU_ITEM = {
  key: "watchlist",
  icon: lookIcon(<EyeOutlined />, Eye),
  name: "page_title.watchlist_and_markets",
  link: WATCHLIST_PATH,
  routerMatch: WATCHLIST_PATH,
  group: SiderGroup.MONITORING,
};
export const MARKETPLACE_MENU_ITEM = {
  key: AppScreen.MARKETPLACE,
  icon: lookIcon(<ShopOutlined />, Store),
  name: "page_title.marketplace",
  link: MARKETPLACE_PATH,
  routerMatch: MARKETPLACE_PATH,
  group: SiderGroup.TRADING,
};
export const MY_GROUPS_MENU_ITEM = {
  key: AppScreen.MY_GROUPS,
  icon: lookIcon(<StyledGroupsIcon />, UsersRound),
  name: "page_title.my_groups",
  link: MY_GROUPS_PATH,
  routerMatch: MY_GROUPS_PATH,
  group: SiderGroup.OTHER,
};
const getBotsSubMenu = (botType: BotType) => {
  if (
    ![
      BotType.SIGNAL,
      BotType.ASAP,
      BotType.TRADING_VIEW,
      BotType.HEDGING,
    ].includes(botType)
  ) {
    return;
  }
  const myBotsLink = BOT_TYPE_TO_BASE_MY_BOTS_PATH[botType];
  const baseMyBotsLink = BOT_TYPE_TO_BASE_PATH[botType];
  const backtestingLink = BOT_TYPE_TO_BASE_BACKTESTING_PATH[botType];
  const subMenuItems: MenuItemConfig[] = [
    {
      key: getMyBotItemKey(botType),
      name: "page_title.bots.my_bots",
      link: myBotsLink,
      routerMatch: [myBotsLink, baseMyBotsLink],
      exactMatch: true,
    },
  ];
  if (botType === BotType.ASAP) {
    subMenuItems.push({
      key: `public-${botType}-bots`,
      name: "page_title.bots.public_bots",
      link: DCA_PUBLIC_BOTS_PATH,
      routerMatch: DCA_PUBLIC_BOTS_PATH,
    });
  }
  if (![BotType.TRADING_VIEW, BotType.HEDGING].includes(botType)) {
    subMenuItems.push({
      key: getBacktestingItemKey(botType),
      name: "page_title.bots.backtesting",
      link: backtestingLink,
      routerMatch: backtestingLink,
    });
  }
  if (botType === BotType.TRADING_VIEW) {
    const myBotsPath =
      BOT_TYPE_TO_BASE_MY_BOTS_PATH[BotType.TRADING_VIEW_SIGNALS];
    subMenuItems.push({
      key: AppScreen.TRADINGVIEW_TO_SIGNAL,
      name: "page_title.bots.trading_view_signals",
      link: myBotsPath,
      routerMatch: myBotsPath,
    });
  }
  return subMenuItems;
};
const SIDER_ICON_SIZE = 16;
const BOT_TYPE_TO_MENU_TITLE: Record<
  BotType,
  TranslationKeys<LanguageNameSpaces.COMMON>
> = {
  [BotType.SIGNAL]: "page_title.bots.signals_bots",
  [BotType.ASAP]: "page_title.bots.dca_bots",
  [BotType.TRADING_VIEW]: "page_title.bots.trading_view_bots",
  [BotType.GRID]: "page_title.bots.grid_bots",
  [BotType.TRADING_VIEW_SIGNALS]: "page_title.bots.trading_view_signals_bots",
  [BotType.HEDGING]: "page_title.bots.hedging_bots",
};
export const getBotsMenuItems = (
  isBottomNavigator?: boolean,
  isDarkMode?: boolean,
  isReadWriteSubUser?: boolean
) => {
  const botTypes = isReadWriteSubUser
    ? [BotType.SIGNAL]
    : BOT_TYPES.filter((botType) => {
        return botType !== BotType.TRADING_VIEW_SIGNALS;
      });
  return botTypes.map((botType) => {
    const botTypeToIcon = isBottomNavigator
      ? isDarkMode
        ? DARK_BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON
        : BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON
      : BOT_TYPE_TO_ICON;
    const Icon = botTypeToIcon[botType];
    const iconSize = isBottomNavigator ? 30 : SIDER_ICON_SIZE;
    const isSignalsBot = botType === BotType.SIGNAL;
    const link = BOT_TYPE_TO_BASE_PATH[botType];
    const routerMatch = [link];
    if (botType === BotType.TRADING_VIEW) {
      routerMatch.push(BOT_TYPE_TO_BASE_PATH[BotType.TRADING_VIEW_SIGNALS]);
    }

    return {
      key: getBotItemKey(botType),
      icon: (
        <LookIcon
          classic={<Icon style={{ width: iconSize, height: iconSize }} />}
          newLook={
            isBottomNavigator ? (
              <NewLookBotIconTile>
                {newLookIcon(NEW_LOOK_BOT_TYPE_TO_ICON[botType])}
              </NewLookBotIconTile>
            ) : (
              newLookIcon(NEW_LOOK_BOT_TYPE_TO_ICON[botType])
            )
          }
        />
      ),
      name: BOT_TYPE_TO_MENU_TITLE[botType],
      link,
      routerMatch,
      group: SiderGroup.TRADING,
      subName: isSignalsBot ? "(Auto-Trading)" : undefined,
      className: isSignalsBot ? "with-sub-name" : undefined,
      subMenu: getBotsSubMenu(botType),
    };
  });
};
export const BOTS_MENU_ITEMS = getBotsMenuItems();
export const AFFILIATION_MENU_ITEM = {
  key: AppScreen.AFFILIATION,
  icon: lookIcon(<CrownOutlined />, Crown),
  name: "page_title.affiliation",
  link: AFFILIATION_PATH,
  routerMatch: AFFILIATION_PATH,
  group: SiderGroup.OTHER,
};
export const FINANCE_MENU_ITEM = {
  key: AppScreen.FINANCE,
  icon: lookIcon(<BankOutlined />, Landmark),
  name: "page_title.finance",
  link: FINANCE_PATH,
  routerMatch: FINANCE_PATH,
  group: SiderGroup.OTHER,
};
export const MCP_MENU_ITEM = {
  key: AppScreen.MCP,
  icon: lookIcon(<ApiOutlined />, Plug),
  name: "page_title.mcp",
  link: MCP_PATH,
  routerMatch: MCP_PATH,
  featureTag: "BETA",
  group: SiderGroup.AI,
};
export const getTradingViewToSignalsMenuItem = () => {
  const Icon = BOT_TYPE_TO_ICON[BotType.TRADING_VIEW];
  return {
    key: AppScreen.TRADINGVIEW_TO_SIGNAL,
    icon: (
      <LookIcon
        classic={
          <Icon style={{ width: SIDER_ICON_SIZE, height: SIDER_ICON_SIZE }} />
        }
        newLook={newLookIcon(
          NEW_LOOK_BOT_TYPE_TO_ICON[BotType.TRADING_VIEW_SIGNALS]
        )}
      />
    ),
    name: "page_title.bots.trading_view_signals",
    link: BOT_TYPE_TO_BASE_MY_BOTS_PATH[BotType.TRADING_VIEW_SIGNALS],
    routerMatch: BOT_TYPE_TO_BASE_PATH[BotType.TRADING_VIEW_SIGNALS],
  };
};
export const ADMIN_TRADING_CONFIGURATION_MENU_ITEM = {
  key: AppScreen.ADMIN_TRADING_CONFIGURATION,
  icon: lookIcon(<Sliders />, SlidersHorizontal),
  name: "page_title.trading_configuration",
  link: ADMIN_TRADING_CONFIGURATION_PATH,
  routerMatch: ADMIN_TRADING_CONFIGURATION_PATH,
};
export const SIGNALS_TERMINAL_MENU_ITEM = {
  icon: lookIcon(<FundProjectionScreenOutlined />, Monitor),
  key: AppScreen.SIGNAL_TERMINAL,
  name: "page_title.bots.signals_terminal",
  link: CREATE_SIGNAL_PATH,
  routerMatch: CREATE_SIGNAL_PATH,
};

const NewLookBotIconTile = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => p.theme.colors.lightBlue};
`;
